import { SagaIterator } from 'redux-saga'
import { put, takeLatest, select, all, race, take } from 'redux-saga/effects'

import getErrorMessageFromResponse from '@tabeeb/shared/utils/getErrorMessageFromResponse'
import { onAddErrorNotification } from '@tabeeb/modules/notification/actions'
import { getContentId } from '@tabeeb/modules/shared/content/selectors'
import { getCurrentUserId } from '@tabeeb/modules/account/selectors'

import * as rawActions from '../actions'
import { ExportFormsToCsvOptions, DisplayAnswersMode } from '../enums'
import { getCurrentForm, getDisplayAnswersMode, getSelectedGalleryItemId, getSelectedUserId } from '../selectors'

function* exportFormToPdfError({ response }: ReturnType<typeof rawActions.apiExportFormToPdf.failed>): SagaIterator {
  const message = getErrorMessageFromResponse(response.data)
  yield put(onAddErrorNotification({ message }))
}

function exportFormAnswers(action: ReturnType<typeof rawActions.apiExportFormToCsv.success>) {
  const { data } = action.response
  const link = data
  window.open(link)
}

function* exportFormToCsv(action: ReturnType<typeof rawActions.exportFormToCsv>): SagaIterator {
  const contentId: ReturnType<typeof getContentId> = yield select(getContentId)
  const selectedOption = action.payload.option ?? ExportFormsToCsvOptions.All

  const currentForm: ReturnType<typeof getCurrentForm> = yield select(getCurrentForm)
  const displayAnswersMode: ReturnType<typeof getDisplayAnswersMode> = yield select(getDisplayAnswersMode)
  const selectedUserId: ReturnType<typeof getSelectedUserId> = yield select(getSelectedUserId)
  const pageId: ReturnType<typeof getSelectedGalleryItemId> = yield select(getSelectedGalleryItemId)
  const currentUserId: ReturnType<typeof getCurrentUserId> = yield select(getCurrentUserId)

  if (!currentForm?.ContentFormId) {
    return
  }

  let userId
  let selectedPageId
  switch (displayAnswersMode) {
    case DisplayAnswersMode.AllLatest:
      userId = null
      selectedPageId = null
      break
    case DisplayAnswersMode.AllMyLatest:
      userId = currentUserId
      selectedPageId = null
      break
    case DisplayAnswersMode.ByUser:
      userId = selectedUserId
      selectedPageId = pageId
      break
    default:
  }

  yield put(
    rawActions.apiExportFormToCsv.request({
      contentId,
      userId,
      pageId: selectedPageId,
      formsIds: selectedOption === ExportFormsToCsvOptions.CurrentForm ? [currentForm.FormId!] : null,
    })
  )
}

function* exportFormToPdf(): SagaIterator {
  const currentForm: ReturnType<typeof getCurrentForm> = yield select(getCurrentForm)
  const displayAnswersMode: ReturnType<typeof getDisplayAnswersMode> = yield select(getDisplayAnswersMode)
  const selectedUserId: ReturnType<typeof getSelectedUserId> = yield select(getSelectedUserId)
  const pageId: ReturnType<typeof getSelectedGalleryItemId> = yield select(getSelectedGalleryItemId)
  const currentUserId: ReturnType<typeof getCurrentUserId> = yield select(getCurrentUserId)

  if (!currentForm) {
    return
  }

  const { selectedTabs } = currentForm

  let userId
  let selectedPageId
  switch (displayAnswersMode) {
    case DisplayAnswersMode.AllLatest:
      userId = null
      selectedPageId = null
      break
    case DisplayAnswersMode.AllMyLatest:
      userId = currentUserId
      selectedPageId = null
      break
    case DisplayAnswersMode.ByUser:
      userId = selectedUserId
      selectedPageId = pageId
      break
    default:
  }

  yield put(
    rawActions.apiExportFormToPdf.request({
      userId,
      contentFormId: currentForm.ContentFormId!,
      formTabs: selectedTabs,
      pageId: selectedPageId,
    })
  )
}

function* handleExportFormToCustomCsv(): SagaIterator {
  const contentForm: ReturnType<typeof getCurrentForm> = yield select(getCurrentForm)
  if (!contentForm) {
    return
  }

  const contentId: ReturnType<typeof getContentId> = yield select(getContentId)

  yield put(
    rawActions.apiExportFormToCustomCsv.request({
      contentId,
      contentFormId: contentForm.ContentFormId!,
    })
  )

  const { success } = yield race({
    success: take(rawActions.apiExportFormToCustomCsv.success),
    failed: take(rawActions.apiExportFormToCustomCsv.failed),
  })

  if (success) {
    const linkToReport = success.response.data

    window.open(linkToReport)
  } else {
    yield put(onAddErrorNotification({ message: 'Invalid form is selected' }))
  }
}

function* exportForm() {
  yield all([
    takeLatest(rawActions.exportFormToCsv, exportFormToCsv),
    takeLatest(rawActions.exportFormToCustomCsv, handleExportFormToCustomCsv),
    takeLatest(rawActions.exportFormToPdf, exportFormToPdf),
    takeLatest(rawActions.apiExportFormToPdf.failed, exportFormToPdfError),
    takeLatest(rawActions.apiExportFormToCsv.success, exportFormAnswers),
  ])
}

export default exportForm
